/* eslint-disable @next/next/no-img-element */
import React, { useState, useRef, useEffect } from 'react';
import { useForm, SubmitHandler, FormProvider } from 'react-hook-form';
import Link from 'next/link';

import { ClientApiService } from 'services/ClientApiService';
import { Button, ButtonVariant, ButtonType, ButtonSize } from 'components/Button';
import { arrowRight, informationCircle, questionCircle } from 'icons/default';
import { loader } from 'icons/misc';
import { InputCheckboxSize } from 'components/InputCheckbox/InputCheckbox';
import { InputText } from 'components/InputText';
import { InputNumber } from 'components/InputNumber';
import { InputSelect } from 'components/InputSelect';
import { Modal } from 'components/Modal';
import { Icon } from 'components/tokens/Icon';
import { Colors } from 'enums/Colors';
import { InputCheckbox } from 'components/InputCheckbox';
import { reloanKycQuestions } from 'utils/kycData';
import { isEmpty } from 'utils/isEmpty';
import useUser from 'lib/auth/useUser';
import { CarLogo } from 'components/CarLogo';
import {
  licensePlateRegex,
  phoneRegex,
  ssnRegex,
} from 'components/sections/Calculate/actions/validation';
import { countries } from 'utils/countries';

import styles from './ReloanApplication.module.scss';

interface User {
  phone?: string;
  email?: string;
  ssn?: string;
  isLoggedIn: boolean;
}

// Typdefinition för formuläret
interface ReloanForm {
  license_plate: string;
  current_loan_amount: string;
  phone_number: string;
  email: string;
  ssn: string;
  income: string;
  employment: string;
  living: string;
  number_of_adults: string;
  number_of_children: string;
  pep: string;
  other_loans: string;
  has_foreign_tax_residency: string;
  foreign_tax_residency_country_code?: string;
  has_foreign_citizenship: string;
  foreign_citizenship_country_code?: string;
  repayment_source: string;
  pep_title?: string;
  pep_title_relative?: string;
  pep_name_relative?: string;
  pep_ssn_relative?: string;
  pep_associated_type_relative?: string;
  terms_and_conditions: boolean;
}

export const ReloanApplication: React.FC = () => {
  const { user } = useUser() as { user: User | undefined };

  const iconColorDark = Colors.DarkBlue;
  const scollToRef = useRef<null | HTMLDivElement>(null);

  const processTextFieldToNumber = (text: string) => parseInt(text.replace(/\D/g, ''), 10);

  // Explicit användning av ReloanForm för att tillfredsställa ESLint
  const defaultFormValues: ReloanForm = {
    license_plate: '',
    current_loan_amount: '',
    phone_number: user?.phone || '',
    email: user?.email || '',
    ssn: user?.ssn || '',
    income: '',
    employment: '',
    living: '',
    number_of_adults: '',
    number_of_children: '',
    pep: '',
    other_loans: '',
    terms_and_conditions: false,
    has_foreign_tax_residency: '',
    foreign_tax_residency_country_code: '',
    has_foreign_citizenship: '',
    foreign_citizenship_country_code: '',
    repayment_source: '',
    pep_title: '',
    pep_title_relative: '',
    pep_name_relative: '',
    pep_associated_type_relative: '',
  };

  const methods = useForm<ReloanForm>({
    defaultValues: defaultFormValues,
  });
  const {
    handleSubmit,
    watch,
    trigger,
    reset,
    formState: { errors, isSubmitted },
  } = methods;

  const taxDomicileValue = methods.watch('has_foreign_tax_residency');
  const hasForeignCitizenship = methods.watch('has_foreign_citizenship');
  const pep = methods.watch('pep');
  // Återställ extra fält om användren tooglar ja / nej
  useEffect(() => {
    if (taxDomicileValue === 'Nej') {
      methods.setValue('foreign_tax_residency_country_code', '');
    }

    if (hasForeignCitizenship === 'Nej') {
      methods.setValue('foreign_citizenship_country_code', '');
    }

    if (pep === 'Nej') {
      methods.setValue('pep_title', '');
      methods.setValue('pep_title_relative', '');
      methods.setValue('pep_name_relative', '');
      methods.setValue('pep_ssn_relative', '');
      methods.setValue('pep_associated_type_relative', '');
    }
  }, [taxDomicileValue, hasForeignCitizenship, pep, methods]);

  const [isLoading, setLoading] = useState(false);

  const [success, setSuccess] = useState(false);
  const [generalError, setGeneralError] = useState(false);
  const [currentStep, setCurrentStep] = useState(1);
  const [monthlyCostExample, setMonthlyCostExample] = useState<number | null>(null);
  const [intrestRate, setInterestRate] = useState<number | null>(null);
  const [effectiveIntreset, setEffectiveInterest] = useState<number | null>(null);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [valuation, setValuation] = useState<number | null>(null);
  const [paymentPlan, setPaymentPlan] = useState<boolean | null>(null);
  const [blockedCar, setBlockedCar] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [brand, setBrand] = useState('');
  const [model, setModel] = useState('');
  const [modelYear, setModelYear] = useState('');

  const licenseValue = watch('license_plate');
  const currentDeptValue = watch('current_loan_amount');

  useEffect(() => {
    if (user) {
      reset({
        ...defaultFormValues,
        phone_number: user.phone || '',
        email: user.email || '',
        ssn: user.ssn || '',
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, reset]);

  const approvedCarCheck = (valuationCheck: number, currentLoan: number) => {
    if (valuationCheck < currentLoan) {
      setBlockedCar(true);
    }
  };

  const handleClickInitial = async () => {
    const isLicensePlateValid = await trigger('license_plate');
    const isCurrentDeptValid = await trigger('current_loan_amount');

    if (!isLicensePlateValid || !isCurrentDeptValid) {
      return;
    }
    try {
      setLoading(true);
      const response = await ClientApiService.checkCarReloan(
        licenseValue,
        'reloan',
        '',
        processTextFieldToNumber(currentDeptValue),
        processTextFieldToNumber('1500')
      );
      const responseJson = await response.json();
      console.log(responseJson);
      if (responseJson.response?.errors && responseJson.response.errors.length > 0) {
        setLoading(false);
        setGeneralError(true);
        setErrorMessage(responseJson.response?.errors[0]);
        setCurrentStep(6);
        return;
      }
      const installmentPlanExample = isEmpty(
        responseJson.response.payload?.reloan_installment_plan_example
      );

      setPaymentPlan(!installmentPlanExample);

      setValuation(responseJson.response.payload?.valuation.price_company);
      setMonthlyCostExample(
        Math.round(
          responseJson.response.payload?.reloan_installment_plan_example.first_monthly_payment
        )
      );
      setBrand(responseJson.response.payload?.brand);
      setModel(responseJson.response.payload?.model);
      setModelYear(responseJson.response.payload?.model_year);

      approvedCarCheck(
        responseJson.response.payload?.valuation.price_company,
        processTextFieldToNumber(currentDeptValue)
      );
      setInterestRate(responseJson.response.payload?.reloan_installment_plan_example.interest_rate);
      setEffectiveInterest(
        responseJson.response.payload?.reloan_installment_plan_example
          .effective_annual_interest_rate
      );
      setLoading(false);
      setCurrentStep(2);
    } catch (error) {
      // console.error(error);
      setLoading(false);
      setGeneralError(true);
    }
  };

  const handlePersonalDetails = async () => {
    const isCurrentDeptValid = await trigger('phone_number');
    const isLicensePlateValid = await trigger('email');

    const isMilageValid = await trigger('ssn');
    if (!isLicensePlateValid || !isCurrentDeptValid || !isMilageValid) {
      return;
    }

    setCurrentStep(4);
  };

  const handleKyc = async () => {
    const isIncomeValid = await trigger('income');
    const isEmploymentValid = await trigger('employment');
    const isLivingValid = await trigger('living');
    const isNumberOfAdultsValid = await trigger('number_of_adults');
    const isNumberOfChildrenValid = await trigger('number_of_children');

    if (
      !isIncomeValid ||
      !isEmploymentValid ||
      !isLivingValid ||
      !isNumberOfAdultsValid ||
      !isNumberOfChildrenValid
    ) {
      return;
    }

    setCurrentStep(5);
  };

  const handleStep: SubmitHandler<any> = async (data: any) => {
    if (currentStep === 5) {
      const kycValuesAndQuestions = reloanKycQuestions.reduce((acc, question) => {
        // eslint-disable-next-line
        acc[question.name] = {
          question: question.question,
          answer: data[question.name],
        };
        return acc;
      }, {});
      const payload = {
        license_plate: data.license_plate,
        terms_and_conditions: true,
        miles_per_year: processTextFieldToNumber('1500'),
        email: data.email,
        phone_number: data.phone_number,
        ssn: data.ssn,
        current_loan_amount: processTextFieldToNumber(data.current_loan_amount),
        additional_data: kycValuesAndQuestions,
      };
      console.log(payload);
      // return;
      const response = await ClientApiService.proxyRequest({
        method: 'postReloan',
        payload,
      });
      // console.log(response.status);
      if (response.status === 400) {
        setLoading(false);
        setGeneralError(true);
        setErrorMessage('Något gick fel med din förfrågan');
        setCurrentStep(6);
        return;
      }

      const responseJson = await response.json();
      // console.log(payload);
      // console.log(responseJson);

      if (responseJson.body?.errors && responseJson.body.errors.length > 0) {
        setLoading(false);
        setGeneralError(true);

        const errorMessageApi = responseJson.body.errors[0];

        if (errorMessageApi === 'Loan exist') {
          setErrorMessage('du har redan skickat in en låneansökan');
        } else {
          setErrorMessage(errorMessageApi);
        }

        setCurrentStep(6);
        return;
      }
      setLoading(false);
      setSuccess(true);
      setCurrentStep(6);
    }
  };

  const image = <CarLogo brand={brand} vehicleType="car" size={48} />;

  return (
    <div className={styles.wrapper}>
      {currentStep === 1 && (
        <div className={styles.contactWrapper}>
          <h2>Påbörja ansökan</h2>
          <FormProvider {...methods}>
            <form onSubmit={handleSubmit(handleStep)}>
              <span className={styles.inputTitle}>Registreringsnummer</span>
              <InputText
                registerOptions={{
                  required: 'Du måste ange ett giltigt registreringsnummer',
                  pattern: {
                    value: licensePlateRegex,
                    message: 'Du måste ange ett giltigt registreringsnummer',
                  },
                  minLength: {
                    value: 6,
                    message: 'Ett registreringsnummer måste vara 6 tecken långt',
                  },
                }}
                size="medium"
                label="ABC123"
                name="license_plate"
                type="text"
                options={{ maxlength: 6 }}
                uppercase
              />
              <span className={styles.inputTitle}>
                Aktuell skuld på nuvarande billån{' '}
                <div className={styles.details}>
                  <Modal ButtonContent={<Icon color={iconColorDark} icon={informationCircle} />}>
                    <h4>Så här ser du aktuell skuld på ditt billån</h4>
                    <h3>1. Logga in på ditt finansbolags hemsida</h3>
                    <p>
                      Gå till webbplatsen för den bank eller det finansbolag där du har ditt billån.
                      Använd dina inloggningsuppgifter för att logga in.
                    </p>
                    <h3>2. Hitta ditt billån</h3>
                    <p>
                      Gå till sektionen som heter &quot;Mina lån&quot; eller liknande. Klicka sedan
                      på ditt billån.
                    </p>
                    <h3>3. Se din aktuella skuld</h3>
                    <p>
                      Här ser du hur mycket du har kvar att betala. Det kan också kallas
                      &quot;Restskuld&quot; eller &quot;Lösenpris&quot;, beroende på vilket
                      finansbolag du använder.
                    </p>
                  </Modal>
                </div>
              </span>
              <InputNumber
                registerOptions={{
                  required: 'Du måste ange nuvarande billån',
                  pattern: {
                    message: 'Du måste ange nuvarande billån',
                  },
                }}
                size="medium"
                label="Nuvarande billån"
                name="current_loan_amount"
                type="text"
                isNumber
              />

              <div className={styles.separator} />
              <Button
                type={ButtonType.Button}
                variant={ButtonVariant.PrimaryMintSweet}
                stretch
                size={ButtonSize.Large}
                disabled={isLoading}
                trailingIcon={isLoading ? loader : arrowRight}
                onClick={handleClickInitial}
              >
                <span>Se månadskostnad</span>
              </Button>

              <div className={styles.separator} />
              <Modal
                ButtonContent={
                  <div className={styles.basicRequirements}>
                    <span>Grundkrav för att ansöka </span>
                    <Icon icon={questionCircle} size={20} color={Colors.DarkBlue} />
                  </div>
                }
              >
                <h2>Grundkrav</h2>
                <p>
                  För att vi ska kunna bevilja en kredit behöver både du som kund och bilen uppfylla
                  vissa krav.
                </p>
                <h2>Krav för dig som kund</h2>
                <p>
                  <ul style={{ listStyle: 'none', paddingLeft: 0 }}>
                    <li>✔ Minst 18 år gammal</li>
                    <li>✔ Fast anställning</li>
                    <li>✔ Inga betalningsanmärkningar</li>
                    <li>✔ Folkbokförd i Sverige</li>
                  </ul>
                </p>
                <h2>Krav för bilen</h2>
                <p>
                  <ul style={{ listStyle: 'none', paddingLeft: 0 }}>
                    <li>✔ Max 10 år gammal vid finansieringens slut</li>
                    <li>✔ Nuvarande avbetalning finns på bilen</li>
                  </ul>
                </p>
              </Modal>
              <div className={styles.separator} />
              <div className={styles.alweyLogo}>
                <span>I samarbete med</span>
                <a href="https://alwey.se" target="_blank" rel="noopener noreferrer">
                  <img
                    src="/assets/logotype/Alwey-black.png"
                    alt="Alwey logotyp"
                    className={styles.alweyLogo}
                  />
                </a>
              </div>
            </form>
          </FormProvider>
        </div>
      )}
      {currentStep === 2 && paymentPlan && !blockedCar && (
        <div>
          <h2>Månadskostnad</h2>
          <div className={styles.monthlyCostExample}>
            <div className={styles.carInfo}>
              <div className={styles.carInfoImage}>{image}</div>
              <div className={styles.carInfoBrand}>
                {brand} {model} {modelYear}
              </div>
            </div>
            <strong>Ungefärlig månadskostnad</strong>

            <div>
              {monthlyCostExample !== null ? (
                <span className={styles.monthlyCost}>
                  {monthlyCostExample.toLocaleString('sv-SE')} kr
                </span>
              ) : (
                <span className={styles.monthlyCost}>Ingen månadskostnad tillgänglig</span>
              )}
            </div>

            <div className={styles.details}>
              Så har vi räknat
              <Modal ButtonContent={<Icon color={iconColorDark} icon={informationCircle} />}>
                <h4>Så har vi räknat</h4>
                <p>
                  <strong>Löptid: 36 månader</strong>
                  <br />
                  <strong> Restvärde: 55%</strong> <br />
                  <strong> Ränta: {intrestRate}</strong>
                  <br />
                  <strong> Effektiv ränta: {effectiveIntreset}</strong>
                  <br />
                  <br />
                  <p>
                    0 kr uppläggningsavgift <br />
                    0 kr aviavgift <br />
                    Annuitetslån
                  </p>
                  <br />
                  Med ett annuitetslån via Blipp betalar du samma summa varje månad – enkelt och
                  smidigt att hålla koll på.
                </p>
              </Modal>
            </div>
          </div>
          <Button
            type={ButtonType.Button}
            variant={ButtonVariant.PrimaryMintSweet}
            stretch
            size={ButtonSize.Large}
            disabled={isLoading}
            trailingIcon={isLoading ? loader : arrowRight}
            onClick={() => setCurrentStep((prevStep) => prevStep + 1)}
          >
            <span>Gå vidare med ansökan</span>
          </Button>
        </div>
      )}
      {currentStep === 2 && !paymentPlan && (
        <div>
          <div className={styles.noMonthlyCostExample}>
            <h2>Vi kunde inte räkna ut månadskostanden</h2>
            <p>
              Antagligen för att du har ett övervärde i bilen. Gå vidare med din ansökan så hjälper
              vi dig.
            </p>
          </div>
          <Button
            type={ButtonType.Button}
            variant={ButtonVariant.PrimaryMintSweet}
            stretch
            size={ButtonSize.Large}
            disabled={isLoading}
            trailingIcon={isLoading ? loader : arrowRight}
            onClick={() => setCurrentStep((prevStep) => prevStep + 1)}
          >
            <span>Gå vidare med ansökan</span>
          </Button>
        </div>
      )}
      {currentStep === 3 && (
        <div className={styles.contactWrapper}>
          <h2>Kontaktuppgifter</h2>
          <FormProvider {...methods}>
            <form onSubmit={handleSubmit(handleStep)}>
              <span className={styles.inputTitle}>Telefonnummer</span>
              <InputText
                registerOptions={{
                  required: 'Du måste ange ett giltigt telefonnummer',
                  pattern: {
                    value: phoneRegex,
                    message: 'Du måste ange ett giltigt telefonnummer',
                  },
                }}
                size="medium"
                label="Telefonnummer"
                name="phone_number"
                type="text"
              />
              {isSubmitted && errors.phone_number && <p>{errors.phone_number.message}</p>}
              <span className={styles.inputTitle}>E-postadress</span>
              <InputText
                registerOptions={{
                  required: 'Du måste ange en giltig e-postadress',
                  pattern: {
                    value: /^\S+@\S+$/i,
                    message: 'Du måste ange en giltig e-postadress',
                  },
                }}
                size="medium"
                label="E-postadress"
                name="email"
                type="text"
              />
              {!user?.ssn && (
                <>
                  <span className={styles.inputTitle}>Personnummer</span>
                  <InputText
                    registerOptions={{
                      required: 'Du måste ange ett giltigt personnummer',
                      pattern: {
                        value: ssnRegex,
                        message: 'Du måste ange ett giltigt personnummer',
                      },
                    }}
                    size="medium"
                    label="Personnummer"
                    name="ssn"
                    type="text"
                  />
                </>
              )}

              <div className={styles.separator} />
              <Button
                type={ButtonType.Button}
                variant={ButtonVariant.PrimaryMintSweet}
                stretch
                size={ButtonSize.Large}
                disabled={isLoading}
                trailingIcon={isLoading ? loader : arrowRight}
                onClick={handlePersonalDetails}
              >
                <span>Gå vidare med ansökan</span>
              </Button>
            </form>
          </FormProvider>
        </div>
      )}
      {currentStep === 4 && (
        <div className={styles.contactWrapper}>
          <h2>Kundkännedom 1/2</h2>
          <FormProvider {...methods}>
            <form onSubmit={handleSubmit(handleStep)}>
              <span className={styles.inputTitle}>Månadsinkomst före skatt</span>
              <InputNumber
                registerOptions={{
                  required: 'Ange månadsinkomst (före skatt)',
                }}
                size="medium"
                label="Månadsinkomst före skatt"
                name="income"
                type="text"
                isNumber
              />

              <span className={styles.inputTitle}>Sysselsättning</span>
              <InputSelect
                name="employment"
                options={[
                  'Anställd',
                  'Egen företagare',
                  'Pensionär',
                  'Studerande',
                  'Arbetslös/annat',
                ]}
                size="small"
                registerOptions={{
                  required: 'Ange sysselsättning',
                }}
              />

              <span className={styles.inputTitle}>Boendeform</span>
              <InputSelect
                name="living"
                options={['Hyresrätt', 'Villa eller radhus', 'Bostadsrätt', 'Annat boende']}
                size="small"
                registerOptions={{
                  required: 'Ange boendeform',
                }}
              />

              <span className={styles.inputTitle}>Antal vuxna i hushållet</span>
              <InputText
                registerOptions={{
                  required: 'Ange antal vuxna i hushållet',
                }}
                size="medium"
                label="Antal vuxna i hushållet"
                name="number_of_adults"
                type="text"
              />
              <span className={styles.inputTitle}>Antal barn i hushållet</span>
              <InputText
                registerOptions={{
                  required: 'Ange antal barn i hushållet',
                }}
                size="medium"
                label="Antal barn i hushållet"
                name="number_of_children"
                type="text"
              />

              <div className={styles.separator} />
              <Button
                type={ButtonType.Button}
                variant={ButtonVariant.PrimaryMintSweet}
                stretch
                size={ButtonSize.Large}
                disabled={isLoading}
                trailingIcon={isLoading ? loader : arrowRight}
                onClick={handleKyc}
              >
                <span>Gå vidare med ansökan</span>
              </Button>
            </form>
          </FormProvider>
        </div>
      )}
      {currentStep === 5 && (
        <div className={styles.contactWrapper}>
          <h2>Kundkännedom 2/2</h2>
          <FormProvider {...methods}>
            <form onSubmit={handleSubmit(handleStep)}>
              <span className={styles.inputTitle}>
                Har du skattehemvist i annat land än Sverige?
              </span>
              <InputSelect
                name="has_foreign_tax_residency"
                options={['Nej', 'Ja']}
                size="small"
                registerOptions={{
                  required: 'Ange skattehemvist',
                }}
              />

              {taxDomicileValue === 'Ja' && (
                <div className={styles.fadeSlideIn}>
                  <span className={styles.inputTitle}>Vilket land har du skattehemvist i?</span>

                  <InputSelect
                    name="foreign_tax_residency_country_code"
                    options={countries}
                    size="small"
                    registerOptions={{
                      required: 'Ange vilket land du har skattehemvist i',
                    }}
                  />
                </div>
              )}

              <span className={styles.inputTitle}>
                Har du medborgarskap i annat land än Sverige?
              </span>
              <InputSelect
                name="has_foreign_citizenship"
                options={['Nej', 'Ja']}
                size="small"
                registerOptions={{
                  required: 'Ange medborgarskap',
                }}
              />

              {hasForeignCitizenship === 'Ja' && (
                <div className={styles.fadeSlideIn}>
                  <span className={styles.inputTitle}>Vilket land har du medborgarskap i?</span>

                  <InputSelect
                    name="foreign_citizenship_country_code"
                    options={countries}
                    size="small"
                    registerOptions={{
                      required: 'Ange vilket land du har medborgarskap i',
                    }}
                  />
                </div>
              )}

              <span className={styles.inputTitle}>
                Var kommer pengarna för återbetalning ifrån?
              </span>
              <InputSelect
                name="repayment_source"
                options={[
                  'Lön/pension',
                  'Arv/gåva',
                  'Försäljning av egendom',
                  'Företagsvinst',
                  'Hyresintäkt',
                ]}
                size="small"
                registerOptions={{
                  required: 'Ange var pengarna kommer ifrån',
                }}
              />

              <span className={styles.inputTitle}>
                Är du / någon i din närhet en politiskt utsatt person?
              </span>
              <InputSelect
                name="pep"
                options={[
                  'Nej',
                  'Ja, jag är eller har varit PEP',
                  'Ja, jag har nära anknytning till PEP',
                ]}
                size="small"
                registerOptions={{
                  required: 'Ange sysselsättning',
                }}
              />

              {pep === 'Ja, jag är eller har varit PEP' && (
                <div className={styles.fadeSlideIn}>
                  <span className={styles.inputTitle}>Din titel</span>

                  <InputSelect
                    name="pep_title"
                    options={[
                      'Stats-/regeringschef eller minister',
                      'Parlamentsledamot (riksdag/EU)',
                      'Partistyrelseledamot',
                      'Domare i HD/HFD',
                      'Tjänsteman - revision/centralbank',
                      'Ambassadör eller hög officer',
                      'VD/styrelse i statsägt bolag',
                      'Ledning i internationell org.',
                    ]}
                    size="small"
                    registerOptions={{
                      required: 'Ange titel',
                    }}
                  />
                </div>
              )}

              {pep === 'Ja, jag har nära anknytning till PEP' && (
                <div className={styles.fadeSlideIn}>
                  <span className={styles.inputTitle}>Ange vem som är PEP</span>

                  <InputSelect
                    name="pep_associated_type_relative"
                    options={[
                      'Partner eller sambo',
                      'Barn eller barns partner',
                      'Förälder',
                      'Delägare med PEP i bolag/stiftelse/förening',
                    ]}
                    size="small"
                    registerOptions={{
                      required: 'Ange vem som är PEP',
                    }}
                  />

                  <span className={styles.inputTitle}>Titel - nära anknytning</span>

                  <InputSelect
                    name="pep_title_relative"
                    options={[
                      'Stats-/regeringschef eller minister',
                      'Parlamentsledamot (riksdag/EU)',
                      'Partistyrelseledamot',
                      'Domare i HD/HFD',
                      'Tjänsteman - revision/centralbank',
                      'Ambassadör eller hög officer',
                      'VD/styrelse i statsägt bolag',
                      'Ledning i internationell org.',
                    ]}
                    size="small"
                    registerOptions={{
                      required: 'Ange titel på nära anknytning',
                    }}
                  />
                  <span className={styles.inputTitle}>Namn - nära anknytning</span>
                  <InputText
                    registerOptions={{
                      required: 'Ange namn',
                    }}
                    size="medium"
                    label="För och efternamn"
                    name="pep_name_relative"
                    type="text"
                  />
                  <span className={styles.inputTitle}>Födelsedatum - nära anknytning</span>
                  <InputText
                    registerOptions={{
                      required: 'Ange födelsedatum',
                      pattern: {
                        value: /^\d{8}$/,
                        message: 'Ange endast födelsedatum i formatet ÅÅÅÅMMDD',
                      },
                    }}
                    size="medium"
                    label="ÅÅÅÅMMDD"
                    name="pep_ssn_relative"
                    type="text"
                  />
                </div>
              )}

              <span className={styles.inputTitle}>Har du några övriga lån?</span>
              <InputNumber
                registerOptions={{
                  required: 'Ange övriga lån',
                }}
                size="medium"
                label="Ange total lånesumma"
                name="other_loans"
                type="text"
                isNumber
              />
              <div className={styles.separator} />
              <div className={styles['terms-container']}>
                <InputCheckbox
                  registerOptions={{
                    required: 'Du måste godkänna villkoren för att skicka in ansökan',
                  }}
                  size={InputCheckboxSize.Small}
                  label={
                    <>
                      Jag försäkrar att lämnade uppgifter är korrekta, att jag godkänner Blipps{' '}
                      <Link href="/anvandarvillkor" passHref>
                        <a target="_blank" rel="noopener noreferrer">
                          användarvillkor
                        </a>
                      </Link>{' '}
                      och{' '}
                      <Link href="/integritetspolicy">
                        <a target="_blank" rel="noopener noreferrer">
                          integritetspolicy
                        </a>
                      </Link>
                      , och att jag har tagit del av&nbsp; Alwey Finance{' '}
                      <Link href="/assets/download/allmanna-villkor-2023-06-15.pdf">
                        <a target="_blank" rel="noopener noreferrer">
                          allmänna villkor
                        </a>
                      </Link>{' '}
                      och{' '}
                      <Link href="#">
                        <a target="_blank" rel="noopener noreferrer">
                          SEKKI
                        </a>
                      </Link>
                      . Samt att en kreditupplysning (UC) kommer att tas i samband med min ansökan.
                    </>
                  }
                  name="terms_and_conditions"
                />
              </div>
              <div className={styles.separator} />

              <Button
                type={ButtonType.Submit}
                variant={ButtonVariant.PrimaryMintSweet}
                stretch
                size={ButtonSize.Large}
                disabled={isLoading}
                trailingIcon={isLoading ? loader : arrowRight}
              >
                <span>Skicka</span>
              </Button>
            </form>
          </FormProvider>
        </div>
      )}

      {!isLoading && success && currentStep === 6 && (
        <div className={styles.successWrapper} ref={scollToRef}>
          <h2>Ansökan skickad!</h2>
          <p>
            Tack. Din ansökan är skickad!{' '}
            {user?.isLoggedIn
              ? 'Du kan nu följa din ansökan på Mitt Blipp.'
              : 'Logga in på Blipp för att slutföra din ansökan'}
          </p>
          <Button
            type={ButtonType.Button}
            variant={ButtonVariant.PrimaryMintSweet}
            stretch
            size={ButtonSize.Large}
            disabled={isLoading}
            trailingIcon={isLoading ? loader : arrowRight}
            href="/app/dashboard"
          >
            <span>{user?.isLoggedIn ? 'Följ din ansökan' : 'Logga in med BankID'}</span>
          </Button>
        </div>
      )}
      {generalError && currentStep === 6 && (
        <div className={styles.errorWrapper} ref={scollToRef}>
          <h2>Någonting gick fel</h2>
          {errorMessage ? (
            <p>
              {errorMessage === 'Loan already exists.'
                ? 'Du har redan skickat in en ansökan.'
                : errorMessage}
            </p>
          ) : (
            <p>Försök igen senare eller kontakt oss via chatten.</p>
          )}
          <Button
            type={ButtonType.Button}
            variant={ButtonVariant.Primary}
            stretch
            size={ButtonSize.Large}
            disabled={isLoading}
            trailingIcon={isLoading ? loader : arrowRight}
            onClick={() => {
              reset(defaultFormValues);
              setCurrentStep(1);
              setGeneralError(false);
              setErrorMessage('');
            }}
          >
            <span>Tillbaka</span>
          </Button>
        </div>
      )}
      {blockedCar && (
        <div className={styles.errorWrapper} ref={scollToRef}>
          <h2>Tyvärr...</h2>
          <p>
            Tyvärr kan du inte lägga om lånet eftersom det nuvarande lånebeloppet överstiger bilens
            värde.
          </p>
        </div>
      )}
    </div>
  );
};
